import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { edgeInvoke } from '@/lib/edgeInvoke';

async function fetchSignedUrl(playbackId: string) {
  const timestamp = Date.now();
  console.log('[useSignedPlaybackUrl] Requesting signed URL for playbackId:', playbackId);
  
  const response = await edgeInvoke('playback-url', {
    playback_id: playbackId
  });

  if (response.error) {
    throw new Error(response.error);
  }

  if (!response.url) {
    throw new Error('No URL returned from signer');
  }

  // Try to extract expiry from JWT if possible
  let expiresAt: number | undefined;
  try {
    const urlObj = new URL(response.url);
    const token = urlObj.searchParams.get('token');
    if (token) {
      // Decode JWT payload (middle part)
      const payload = JSON.parse(atob(token.split('.')[1]));
      expiresAt = payload.exp;
    }
  } catch (e) {
    console.warn('[useSignedPlaybackUrl] Could not parse JWT token:', e);
  }

  return {
    url: response.url,
    expiresAt,
    timestamp
  };
}

export function useSignedPlaybackUrl(playbackId?: string): {
  url?: string;
  isLoading: boolean;
  error?: Error;
  refetch: () => void;
  lastCall?: {
    status: 'success' | 'error';
    timestamp: number;
    expiresAt?: number;
    error?: string;
  };
} {
  // Memoize signed URL fetch with React Query
  const query = useQuery({
    queryKey: ['muxSignedUrl', playbackId],
    queryFn: () => fetchSignedUrl(playbackId!),
    enabled: !!playbackId,
    staleTime: 4 * 60 * 1000,   // 4 minutes
    gcTime: 10 * 60 * 1000,     // 10 minutes (renamed from cacheTime)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1
  });

  const lastCall = query.data ? {
    status: 'success' as const,
    timestamp: query.data.timestamp,
    expiresAt: query.data.expiresAt
  } : query.error ? {
    status: 'error' as const,
    timestamp: Date.now(),
    error: query.error instanceof Error ? query.error.message : 'Unknown error'
  } : undefined;

  return {
    url: query.data?.url,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : undefined,
    refetch: () => { query.refetch(); },
    lastCall
  };
}